import { useEffect, useId, useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { StatusBadge } from '../common/StatusBadge'
import styles from './ActivityRow.module.css'

/**
 * Compact, expandable status row for executions and other operator activity.
 * The caller owns domain-specific detail; this component owns disclosure and
 * consistent status/meta presentation.
 */
export function ActivityRow({
  as: Component = 'article',
  title,
  eyebrow,
  ordinal,
  status,
  animated = false,
  duration,
  summary,
  progress,
  progressLabel,
  meta = [],
  metaLabel = 'Activity details',
  badges,
  description,
  children,
  onSelect,
  selectLabel,
  actions,
  defaultExpanded = false,
  compact = false,
  className = '',
  ...props
}) {
  const detailId = useId()
  const [expanded, setExpanded] = useState(defaultExpanded)
  const expandable = Boolean(children)

  useEffect(() => {
    if (defaultExpanded) setExpanded(true)
  }, [defaultExpanded])

  const interactive = expandable || Boolean(onSelect)
  const normalizedProgress = Number.isFinite(progress)
    ? Math.max(0, Math.min(100, Number(progress)))
    : null
  const Summary = interactive ? 'button' : 'div'
  const handleSummaryClick = () => {
    if (expandable) {
      setExpanded((current) => !current)
      return
    }
    onSelect?.()
  }

  return (
    <Component
      {...props}
      className={`${styles.row} ${compact ? styles.compact : ''} ${expanded ? styles.expanded : ''} ${className}`.trim()}
      data-layout={compact ? 'compact' : undefined}
    >
      <div className={styles.summaryRow}>
        <Summary
          {...(interactive ? { type: 'button', onClick: handleSummaryClick } : {})}
          className={styles.summary}
          aria-label={selectLabel}
          aria-expanded={expandable ? expanded : undefined}
          aria-controls={expandable ? detailId : undefined}
        >
          <span className={styles.identity}>
            {ordinal !== undefined && ordinal !== null ? <span className={styles.ordinal} aria-hidden="true">{ordinal}</span> : null}
            <span className={styles.heading}>
              {eyebrow ? <span className={styles.eyebrow}>{eyebrow}</span> : null}
              <span className={styles.title}>{title}</span>
              {description ? <span className={styles.description}>{description}</span> : null}
            </span>
          </span>

          {meta.length ? (
            <span className={styles.meta} aria-label={metaLabel}>
              {meta.map((item, index) => (
                <span key={`${item.label || 'value'}-${index}`} className={styles.metaItem}>
                  {item.label ? <span className={styles.metaLabel}>{item.label}</span> : null}
                  <span>{item.value}</span>
                </span>
              ))}
            </span>
          ) : null}

          <span className={styles.trailing}>
            {summary ? <span className={styles.summaryText}>{summary}</span> : null}
            {duration ? <span className={styles.duration}>{duration}</span> : null}
            {badges ? <span className={styles.badges}>{badges}</span> : null}
            {status ? <StatusBadge auto animated={animated}>{status}</StatusBadge> : null}
            {expandable ? <ChevronDown className={styles.chevron} size={16} aria-hidden="true" /> : null}
          </span>
        </Summary>
        {actions ? <div className={styles.rowActions}>{actions}</div> : null}
      </div>

      {expandable && expanded ? (
        <div id={detailId} className={styles.detail}>
          {children}
        </div>
      ) : null}
      {normalizedProgress !== null ? (
        <span
          className={styles.progress}
          role="progressbar"
          aria-label={progressLabel || 'Task progress'}
          aria-valuemin="0"
          aria-valuemax="100"
          aria-valuenow={normalizedProgress}
        >
          <span style={{ width: `${normalizedProgress}%` }} />
        </span>
      ) : null}
    </Component>
  )
}

export default ActivityRow
